import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, FieldData } from 'vopay';

import {
  withStyles,
  Radio,
  TextField,
  Select,
  FinancialInstitutionBranch,
  MultiSelect,
  ElinxNotificationMethod,
  FinancialInstitution,
  PulloverSelect,
  InteracWordmark,
  CLIENT_ACCOUNT_ID_REGEX,
  GoogleAddressField,
} from 'vopay';

import * as Yup from 'yup';
import AddLabelDialog from './AddLabelDialog/AddLabelDialog';
import { Button, SelectOptionLocked } from 'UI';
import useLabels from '@/hooks/useApi/useLabels';
import { useTranslation } from 'react-i18next';
import useFetchFinancialInstitutions from '@/hooks/useApi/useFetchFinancialInstitutions';
import useFetchFinancialInstitutionBranches from '@/hooks/useApi/useFetchFinancialInstitutionBranches';
import { useDialog } from '@/providers/Dialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import styles from './CreateCustomerForm.module.scss';

export type PaymentMethodType = 'bank' | 'debitcard' | 'credit' | 'interac' | '';

type AccountType = 'individual' | 'business';

type FiOption = {
  id: string;
  number: string;
  label: string;
};

type FiBranchOption = {
  transitNumber: string;
  label: string;
};

type ConnectionType = 'none' | 'provide' | 'request';

type BankValues = {
  // CA bank
  bankCurrency: 'CAD' | 'USD' | '';
  fiID: string;
  branchTransitNo: string;

  // US bank
  ABARoutingNumber: string;

  // CA/US bank
  accountNum: string;
};

type InteracDeliveryMethodType = 'email' | 'phone' | '';

type InfoCollectionMethodType = 'manual' | 'processorToken' | 'flinksCredentials';

type InteracValues = {
  interacDeliveryMethod: InteracDeliveryMethodType;
  interacEmail: string;
  interacPhone: string;
  interacQuestion: string;
  interacAnswer: string;
};

type PaymentMethodValues = BankValues &
  InteracValues & {
    connectionType: ConnectionType;

    // connectionType === 'provide'
    providedPaymentMethod: PaymentMethodType;

    // connectionType === 'request'
    requestedPaymentMethods: PaymentMethodType[];
    accountSelectionMethods: ('online' | 'manual')[];
    notificationMethod: Extract<ElinxNotificationMethod, 'email' | 'sms'> | 'none' | '';
    // connectionType === 'none'

    // This is used to determine how the information is collected
    infoCollectionMethod: InfoCollectionMethodType;
  };

export type FormValues = PaymentMethodValues & {
  accountType: AccountType;
  firstName: string;
  lastName: string;
  businessName: string;
  email: string;
  phone: string;
  accountID: string;
  label: string;

  country: string;
  address1: string;
  city: string;
  province: string;
  postalCode: string;

  plaidProcessorToken: string;
  flinksLoginID: string;
  flinksAccountID: string;
};

export type SerializedFormValues = Omit<
  FormValues,
  'requestedPaymentMethods' | 'accountSelectionMethods' | 'notificationMethod'
> & {
  requestedPaymentMethods: string;
  accountSelectionMethods: string;
  notificationMethod?: ElinxNotificationMethod;
};

export type MoreInfoConfigProps<T extends object> =
  | {
      moreInfoConfig?: never;
      moreInfoInitialValues?: never;
    }
  | {
      moreInfoConfig: FieldData<FormValues & T>[];
      moreInfoInitialValues: {
        [K in keyof T]: T[K];
      };
    };

type UseFormConfigProps<T extends object> = {
  customerLabel: string;
  reviewing: boolean;
  onSubmit: (values: SerializedFormValues & T) => Promise<void>;
} & MoreInfoConfigProps<T>;

const cn = withStyles(styles);

export default function useFormConfig<T extends object>({
  customerLabel,
  moreInfoConfig: moreInfoConfigProp = [],
  moreInfoInitialValues = {} as T,
  reviewing,
  onSubmit,
}: UseFormConfigProps<T>) {
  const {
    response: { Country: loginUserCountry, clientAccountsEnabled },
  } = useClientData();

  const dialog = useDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.Create' });
  const { t: tCountries } = useTranslation('translations', { keyPrefix: 'Global.Countries' });
  const { fetchingLabels, labels } = useLabels();
  const [fiRes, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [fiBranchRes, fetchFiBranches] = useFetchFinancialInstitutionBranches({});
  const [fiOptions, setFiOptions] = useState<FiOption[]>([]);
  const [fiBranchOptions, setFiBranchOptions] = useState<FiBranchOption[]>([]);
  const { CustomFlinksCredentialsSet } = useLockedFeatures();

  useEffect(() => {
    if (fiRes.Success) {
      setFiOptions(Object.values(fiRes.FinancialInstitutions || {}).map(convertToFiOption));
    } else {
      setFiOptions([]);
    }
  }, [fiRes]);

  useEffect(() => {
    if (fiBranchRes.Success) {
      setFiBranchOptions(Object.values(fiBranchRes.Branches || {}).map(convertToFiBranchOption));
    } else {
      setFiBranchOptions([]);
    }
  }, [fiBranchRes]);

  const initialBankValues: BankValues = {
    bankCurrency: '',
    fiID: '',
    branchTransitNo: '',

    ABARoutingNumber: '',

    accountNum: '',
  };

  const initialInteracValues: InteracValues = {
    interacDeliveryMethod: '',
    interacEmail: '',
    interacPhone: '',
    interacQuestion: '',
    interacAnswer: '',
  };

  const initialPaymentMethodValues: PaymentMethodValues = {
    connectionType: 'none',
    providedPaymentMethod: '',
    infoCollectionMethod: 'manual',

    ...initialBankValues,
    ...initialInteracValues,

    requestedPaymentMethods: [],
    accountSelectionMethods: [],
    notificationMethod: '',
  };

  const initialValues: FormValues & T = {
    accountType: 'individual',
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    phone: '',
    ...moreInfoInitialValues,
    accountID: '',
    label: '',

    country: '',
    address1: '',
    city: '',
    province: '',
    postalCode: '',

    plaidProcessorToken: '',
    flinksLoginID: '',
    flinksAccountID: '',

    ...initialPaymentMethodValues,
  };

  const infoCollectionMethodConfig: FieldData<T & FormValues>[] = [
    {
      fieldName: 'infoCollectionMethod',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<FormValues['infoCollectionMethod']>({});

        return (
          <PulloverSelect
            {...fieldData}
            onChange={async (value) => {
              await ctx.setFieldValue('infoCollectionMethod', value);
            }}
          >
            <PulloverSelect.Option value="manual">{t('InfoCollectionType.manual')}</PulloverSelect.Option>
            <PulloverSelect.Option value="processorToken">
              {t('InfoCollectionType.processorToken')}
            </PulloverSelect.Option>
            {CustomFlinksCredentialsSet && (
              <PulloverSelect.Option value="flinksCredentials">
                {t('InfoCollectionType.flinksCredentials')}
              </PulloverSelect.Option>
            )}
          </PulloverSelect>
        );
      },
    },
  ];

  const accountTypeConfig: FieldData<T & FormValues>[] = [
    {
      label: () =>
        t('accountType', {
          label: customerLabel,
        }),
      fieldName: 'accountType',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        return (
          <Radio
            {...getFormikData<FormValues['accountType']>({
              onChange: (e) => {
                ctx.setValues({
                  ...initialValues,
                  infoCollectionMethod: ctx.values.infoCollectionMethod,
                  accountType: e.target.value,
                });
              },
            })}
            variant="outlineShadow"
            noOrdering
            options={{
              individual: t('AccountType.individual'),
              business: t('AccountType.business'),
            }}
          />
        );
      },
    },
  ];

  const baseConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('businessName'),
      fieldName: 'businessName',
      getInputState: () => 'required',
      showField: () => !isIndividual,
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['businessName']>({})} variant="outlineShadow" />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: 'business',
          then: Yup.string().label(t('businessName')).required(t('Validation.required')),
        }),
    },
    {
      label: () => t('firstName'),
      fieldName: 'firstName',
      getInputState: () => 'required',
      showField: () => isIndividual,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<FormValues['firstName']>({})} />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: (accountType: AccountType) => accountType === 'individual' || clientAccountsEnabled,
          then: () => Yup.string().label(t('firstName')).required(t('Validation.required')),
          otherwise: () => Yup.string().label(t('firstName')),
        }),
    },
    {
      label: () => t('lastName'),
      fieldName: 'lastName',
      getInputState: () => 'required',
      showField: () => isIndividual,
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['lastName']>({})} variant="outlineShadow" />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: (accountType: AccountType) => accountType === 'individual' || clientAccountsEnabled,
          then: () => Yup.string().label(t('lastName')).required(t('Validation.required')),
          otherwise: () => Yup.string().label(t('lastName')),
        }),
    },
    {
      label: () => t('email'),
      fieldName: 'email',
      getInputState: () => 'required',
      showField: (values) => isIndividual && values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['email']>({})} variant="outlineShadow" />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: (accountType: AccountType) => accountType === 'individual' || clientAccountsEnabled,
          then: () => Yup.string().label(t('email')).required(t('Validation.required')),
          otherwise: () => Yup.string().label(t('email')),
        }),
    },
    {
      label: () => t('phone'),
      fieldName: 'phone',
      getInputState: () =>
        clientAccountsEnabled && !isIndividual && ctx.values.interacDeliveryMethod !== 'phone'
          ? 'optional'
          : 'required',
      showField: (values) => values.infoCollectionMethod === 'manual',
      serializeField: (values) => values.phone.replaceAll('-', ''),
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['phone']>({})} variant="outlineShadow" preset="tel" />;
      },
      validate: () =>
        Yup.string().when('interacDeliveryMethod', {
          is: (interacDeliveryMethod: string) =>
            !clientAccountsEnabled || isIndividual || interacDeliveryMethod === 'phone',
          then: Yup.string().label(t('phone')).required(t('Validation.required')),
          otherwise: Yup.string().label(t('phone')).notRequired(),
        }),
    },
    {
      label: () => t('processorToken'),
      fieldName: 'plaidProcessorToken',
      getInputState: () => 'required',
      showField: (values) => values.infoCollectionMethod === 'processorToken',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['plaidProcessorToken']>({})} variant="outlineShadow" />;
      },
      validate: () => Yup.string().label(t('processorToken')).required(t('Validation.required')),
    },
    {
      label: () => t('loginID'),
      fieldName: 'flinksLoginID',
      getInputState: () => 'required',
      showField: (values) => values.infoCollectionMethod === 'flinksCredentials',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['flinksLoginID']>({})} variant="outlineShadow" />;
      },
      validate: () => Yup.string().label(t('loginID')).required(t('Validation.required')),
    },
    {
      label: () => t('flinksAccountID'),
      fieldName: 'flinksAccountID',
      getInputState: () => 'required',
      showField: (values) => values.infoCollectionMethod === 'flinksCredentials',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['flinksAccountID']>({})} variant="outlineShadow" />;
      },
      validate: () => Yup.string().label(t('flinksAccountID')).required(t('Validation.required')),
    },
  ];

  const moreInfoConfig: FieldData<T & FormValues>[] = [
    ...moreInfoConfigProp,
    {
      label: () =>
        t('accountID', {
          label: customerLabel,
        }),
      fieldName: 'accountID',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['accountID']>({})} variant="outlineShadow" />;
      },
      validate: () =>
        Yup.string().matches(CLIENT_ACCOUNT_ID_REGEX, {
          message: t('Validation.accountID', {
            label: customerLabel,
          }),
          excludeEmptyString: true,
        }),
    },
    {
      label: () => t('label'),
      fieldName: 'label',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['label']>({})}
            sort={false}
            showClear
            loading={fetchingLabels}
            options={labels.map((l) => {
              return { value: l, label: l };
            })}
            renderDropdownFooter={({ close }) => (
              <Button
                iconLeft="add"
                style={{ width: '100%' }}
                onClick={() => {
                  close();
                  handleAddLabelClick();
                }}
              >
                {t('newLabel')}
              </Button>
            )}
          />
        );
      },
    },
  ];

  const addressConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('address'),
      fieldName: 'address1',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return (
          <GoogleAddressField
            variant="outlineShadow"
            ISOCountry={['CA', 'US']}
            placeholder=""
            onGoogleAddressSelect={(address) => {
              ctx.setValues((prevValues) => ({
                ...prevValues,
                address1: address.address,
                city: address.city,
                province: address.province,
                postalCode: address.postalcode,
                country: address.country,
              }));
            }}
            {...getFormikData<FormValues['address1']>({})}
          />
        );
      },
    },
    {
      label: () => t('city'),
      fieldName: 'city',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['city']>({})} variant="outlineShadow" />;
      },
    },
    {
      label: () => t('province'),
      fieldName: 'province',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['province']>({})} variant="outlineShadow" />;
      },
    },
    {
      label: () => t('postalCode'),
      fieldName: 'postalCode',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['postalCode']>({})} variant="outlineShadow" />;
      },
    },
    {
      label: () => t('country'),
      fieldName: 'country',
      getInputState: () => 'optional',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return (
          <Select
            {...getFormikData<FormValues['country']>({})}
            version="2"
            showClear
            options={[
              { value: 'CA', label: tCountries('CA') },
              { value: 'US', label: tCountries('USLong') },
            ]}
          />
        );
      },
    },
  ];

  const primaryContactConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('firstName'),
      fieldName: 'firstName',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<FormValues['firstName']>({})} />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: (accountType: AccountType, infoCollectionMethod: InfoCollectionMethodType) =>
            accountType === 'individual' || (clientAccountsEnabled && infoCollectionMethod === 'manual'),
          then: () => Yup.string().label(t('firstName')).required(t('Validation.required')),
          otherwise: () => Yup.string().label(t('firstName')),
        }),
    },
    {
      label: () => t('lastName'),
      fieldName: 'lastName',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['lastName']>({})} variant="outlineShadow" />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: (accountType: AccountType, infoCollectionMethod: InfoCollectionMethodType) =>
            accountType === 'individual' || (clientAccountsEnabled && infoCollectionMethod === 'manual'),
          then: () => Yup.string().label(t('lastName')).required(t('Validation.required')),
          otherwise: () => Yup.string().label(t('lastName')),
        }),
    },
    {
      label: () => t('email'),
      fieldName: 'email',
      getInputState: () => 'required',
      showField: (values) => values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['email']>({})} variant="outlineShadow" />;
      },
      validate: () =>
        Yup.string().when('accountType', {
          is: (accountType: AccountType) => accountType === 'individual' || clientAccountsEnabled,
          then: () => Yup.string().label(t('email')).required(t('Validation.required')),
          otherwise: () => Yup.string().label(t('email')),
        }),
    },
  ];

  const basePaymentMethodOptions: {
    value: PaymentMethodType;
    label: string;
    renderLabel?: ReactNode;
    featureKey: string;
  }[] = [
    { value: 'bank', label: t('PaymentMethod.bank'), featureKey: 'Bank Account' },
    { value: 'credit', label: t('PaymentMethod.credit'), featureKey: 'Credit Card' },
    { value: 'debitcard', label: t('PaymentMethod.debitcard'), featureKey: 'Debit Card' },
  ];

  const providePaymentMethodOptions = useMemo(() => {
    if (loginUserCountry === 'US') return basePaymentMethodOptions;

    return [
      ...basePaymentMethodOptions,
      {
        value: 'interac',
        label: 'INTERAC e-Transfer',
        featureKey: 'Email',
        renderLabel: <InteracWordmark />,
      },
    ];
  }, [loginUserCountry]);

  const connectionTypeConfig: FieldData<T & FormValues>[] = [
    {
      fieldName: 'connectionType',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<FormValues['connectionType']>({});

        return (
          <PulloverSelect
            {...fieldData}
            onChange={async (value) => {
              await ctx.setValues({
                ...ctx.values,
                ...initialPaymentMethodValues,
                connectionType: value,
              });

              if (value === 'provide') {
                ctx.setFieldValue('providedPaymentMethod', 'bank');
              }
            }}
          >
            <PulloverSelect.Option value="none">{t('ConnectionType.none')}</PulloverSelect.Option>
            <PulloverSelect.Option value="provide">{t('ConnectionType.provide')}</PulloverSelect.Option>
            <PulloverSelect.Option value="request">{t('ConnectionType.request')}</PulloverSelect.Option>
          </PulloverSelect>
        );
      },
    },
  ];

  const requestPaymentMethodConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('paymentMethod'),
      fieldName: 'requestedPaymentMethods',
      getInputState: () => 'required',

      showField: (values) => values.connectionType === 'request' && values.infoCollectionMethod === 'manual',
      serializeField: (values) => values.requestedPaymentMethods.join(','),
      validate: () => Yup.array().of(Yup.string()).min(1, t('Validation.required')),
      renderField({ ctx, getFormikData }) {
        return (
          <MultiSelect
            {...getFormikData<FormValues['requestedPaymentMethods']>({
              onChange: (e) => {
                const values = e.target.value.split(',').filter(Boolean);
                ctx.setFieldValue(e.target.name, values);
              },
            })}
            showClear
            options={basePaymentMethodOptions}
            sort={false}
          />
        );
      },
    },
    {
      label: () => t('accountSelectionMethods'),
      fieldName: 'accountSelectionMethods',
      getInputState: () => 'required',
      showField: (values) =>
        values.connectionType === 'request' &&
        values.requestedPaymentMethods.includes('bank') &&
        values.infoCollectionMethod === 'manual',
      serializeField: (values) =>
        values.accountSelectionMethods.length > 1 ? 'any' : values.accountSelectionMethods.join(','),
      validate: () => Yup.array().of(Yup.string()).min(1, t('Validation.required')),
      renderField: ({ ctx, getFormikData }) => {
        return (
          <MultiSelect
            {...getFormikData<FormValues['accountSelectionMethods']>({
              onChange: (e) => {
                const values = e.target.value.split(',').filter(Boolean);
                ctx.setFieldValue(e.target.name, values);
              },
            })}
            showClear
            options={[
              { value: 'online', label: t('AccountSelectionMethods.online') },
              { value: 'manual', label: t('AccountSelectionMethods.manual') },
            ]}
            sort={false}
          />
        );
      },
    },
    {
      label: () => t('notificationMethod'),
      fieldName: 'notificationMethod',
      getInputState: () => 'required',
      showField: (values) => values.connectionType === 'request' && values.infoCollectionMethod === 'manual',
      serializeField: (values) => (values.notificationMethod === 'none' ? undefined : values.notificationMethod),
      validate: () => Yup.string().required(t('Validation.required')),
      renderField: ({ getFormikData }) => {
        return (
          <Radio
            {...getFormikData<FormValues['notificationMethod']>({})}
            variant="outlineShadow"
            noOrdering
            options={{
              none: t('NotificationMethod.none'),
              email: t('NotificationMethod.email'),
              sms: t('NotificationMethod.sms'),
            }}
          />
        );
      },
    },
  ];

  const provideBankConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('bankCurrency'),
      fieldName: 'bankCurrency',
      getInputState: () => (loginUserCountry === 'CA' ? 'required' : 'disabled'),
      showField: (values) =>
        values.providedPaymentMethod === 'bank' &&
        loginUserCountry === 'CA' &&
        values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return (
          <Select
            {...getFormikData<FormValues['bankCurrency']>({})}
            version="2"
            options={[
              { value: 'CAD', label: 'CAD', featureKey: '' },
              { value: 'USD', label: 'USD', featureKey: 'USD' },
            ]}
            renderOption={(o, args) => (
              <SelectOptionLocked {...args} featureKey={o.featureKey}>
                {o.label}
              </SelectOptionLocked>
            )}
          />
        );
      },
      validate: () => Yup.string().required(t('Validation.required')),
    },
    {
      label: () => t('fiID'),
      // financial ins num is not unique so we need to use the ID
      fieldName: 'fiID',
      serializeField: (values) => getFiNum(values.fiID),
      getInputState: () => 'required',
      showField: (values) =>
        values.providedPaymentMethod === 'bank' &&
        loginUserCountry === 'CA' &&
        values.infoCollectionMethod === 'manual',
      renderField: ({ ctx, getFormikData }) => {
        return (
          <Select
            {...getFormikData<FormValues['fiID']>({
              onChange: async (e) => {
                ctx.setFieldValue('branchTransitNo', initialPaymentMethodValues.branchTransitNo);

                const fiID = Number(e.target.value);

                if (isNaN(fiID) || !fiID) {
                  setFiBranchOptions([]);
                } else if (fiID) {
                  fetchFiBranches({ FinancialInstitutionID: fiID });
                }
              },
            })}
            version="2"
            showClear
            filterable
            onFilter={(e) => {
              handleFiFilter(e.filter);
            }}
            sort={false}
            limit={20}
            options={fiOptions}
            optionValue={(o) => o.id}
            optionLabel={(o) => [o.number, o.label].filter((val) => !!val).join(' ')}
            emptyFilterMsg="No results. Manually enter a number."
            renderOption={(fi, { Option }) => (
              <Option>
                {fi.number} <br /> {fi.label}
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.string().required(t('Validation.required')),
    },
    {
      label: () => t('branchTransitNo'),
      fieldName: 'branchTransitNo',
      getInputState: (ctx) => (ctx.values.fiID ? 'required' : 'disabled'),
      showField: (values) =>
        values.providedPaymentMethod === 'bank' &&
        loginUserCountry === 'CA' &&
        values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return (
          <Select
            {...getFormikData<FormValues['branchTransitNo']>({})}
            version="2"
            showClear
            filterable
            onFilter={(e) => handleFiBranchFilter(e.filter)}
            limit={20}
            options={fiBranchOptions}
            optionValue={(branch) => branch.transitNumber}
            optionLabel={(branch) => [branch.transitNumber, branch.label].filter((val) => !!val).join(' ')}
            emptyFilterMsg="No results. Manually enter a number."
            renderOption={(branch, { Option }) => (
              <Option>
                {branch.transitNumber} <br />
                {branch.label}
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.string().required(t('Validation.required')),
    },
    {
      label: () => t('ABARoutingNumber'),
      fieldName: 'ABARoutingNumber',
      showField: (values) =>
        values.providedPaymentMethod === 'bank' &&
        loginUserCountry === 'US' &&
        values.infoCollectionMethod === 'manual',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        return (
          <TextField
            {...getFormikData<FormValues['ABARoutingNumber']>({})}
            variant="outlineShadow"
            preset="positiveInteger"
          />
        );
      },
      validate: () =>
        Yup.string()
          .required(t('Validation.required'))
          .test('aba', t('Validation.ABARoutingNumber'), (value) => {
            const match = /^\s*([\d]{9})\s*$/.exec(value || '');

            if (!match) {
              return false;
            }

            const weights = [3, 7, 1];
            const aba = match[1];

            let sum = 0;

            for (let i = 0; i < 9; ++i) {
              // using charAt for IE7 support
              sum += Number(aba.charAt(i)) * weights[i % 3];
            }

            return sum !== 0 && sum % 10 === 0;
          }),
    },
    {
      label: () => t('accountNum'),
      fieldName: 'accountNum',
      getInputState: () => 'required',
      showField: (values) =>
        values.providedPaymentMethod === 'bank' && !!loginUserCountry && values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return (
          <TextField
            {...getFormikData<FormValues['accountNum']>({})}
            variant="outlineShadow"
            preset="positiveInteger"
          />
        );
      },
      validate: () => Yup.string().min(4, t('Validation.accountNum')).required(t('Validation.required')),
    },
  ];

  const provideInteracConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('interacDeliveryMethod'),
      fieldName: 'interacDeliveryMethod',
      getInputState: () => 'required',
      showField: (values) => values.providedPaymentMethod === 'interac' && values.infoCollectionMethod === 'manual',
      validate: () => Yup.string().required(t('Validation.required')),
      renderField: ({ ctx, getFormikData }) => {
        return (
          <Select
            {...getFormikData<FormValues['interacDeliveryMethod']>({
              onChange: (e) => {
                if (e.target.value === 'email') {
                  ctx.setFieldValue('interacEmail', ctx.values.email);
                } else if (e.target.value === 'phone') {
                  ctx.setFieldValue('interacPhone', ctx.values.phone);
                } else {
                  ctx.setValues({
                    ...ctx.values,
                    interacEmail: initialPaymentMethodValues.interacEmail,
                    interacPhone: initialPaymentMethodValues.interacPhone,
                  });
                }
              },
            })}
            version="2"
            options={[
              { value: 'email', label: t('InteracDeliveryMethod.email') },
              { value: 'phone', label: t('InteracDeliveryMethod.phone') },
            ]}
          />
        );
      },
    },
    {
      label: () => t('email'),
      fieldName: 'interacEmail',
      getInputState: () => 'required',
      showField: (values) => values.interacDeliveryMethod === 'email' && values.infoCollectionMethod === 'manual',
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['email']>({})} variant="outlineShadow" />;
      },
      validate: () => Yup.string().email(t('Validation.email')).required(t('Validation.required')),
    },
    {
      label: () => t('phone'),
      fieldName: 'interacPhone',
      getInputState: () => 'required',
      showField: (values) => values.interacDeliveryMethod === 'phone' && values.infoCollectionMethod === 'manual',
      serializeField: (values) => values.phone.replaceAll('-', ''),
      renderField: ({ getFormikData }) => {
        return <TextField {...getFormikData<FormValues['phone']>({})} variant="outlineShadow" preset="tel" />;
      },
      validate: () => Yup.string().required(t('Validation.required')),
    },
    {
      label: () => t('interacQuestion'),
      fieldName: 'interacQuestion',
      getInputState: () => 'required',
      showField: (values) => !!values.interacDeliveryMethod && values.infoCollectionMethod === 'manual',
      validate: () => Yup.string().required(t('Validation.required')),
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<FormValues['interacQuestion']>({})} />;
      },
    },
    {
      label: () => t('interacAnswer'),
      fieldName: 'interacAnswer',
      getInputState: () => 'required',
      showField: (values) => !!values.interacDeliveryMethod && values.infoCollectionMethod === 'manual',
      validate: () => Yup.string().required(t('Validation.required')),
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<FormValues['interacAnswer']>({})} />;
      },
    },
  ];

  const paymentMethodConfig: FieldData<T & FormValues>[] = [
    {
      label: () => t('paymentMethod'),
      fieldName: 'providedPaymentMethod',
      getInputState: () => 'required',
      showField: (values) => values.connectionType === 'provide' && values.infoCollectionMethod === 'manual',
      validate: () => Yup.string().required(t('Validation.required')),
      renderField: ({ ctx, getFormikData }) => {
        return (
          <>
            <Select
              {...getFormikData<FormValues['providedPaymentMethod']>({
                onChange: async (e) => {
                  ctx.setValues({
                    ...ctx.values,
                    ...initialBankValues,
                    ...initialInteracValues,
                    providedPaymentMethod: e.target.value,
                  });

                  if (e.target.value === 'bank' && loginUserCountry === 'CA') {
                    fetchFinancialInstitutions({});
                  }
                },
              })}
              version="2"
              options={providePaymentMethodOptions}
              renderSelectedValue={(o, { Placeholder }) => {
                if (o) return o.renderLabel || o.label;

                return <Placeholder />;
              }}
              renderOption={(o, args) => (
                <SelectOptionLocked {...args} featureKey={o.featureKey}>
                  {o.renderLabel || o.label}
                </SelectOptionLocked>
              )}
            />

            {['credit', 'debitcard'].includes(ctx.values.providedPaymentMethod) && (
              <p className={cn('helpText')}>{t('PaymentMethod.help')}</p>
            )}
          </>
        );
      },
    },
    ...provideBankConfig,
    ...provideInteracConfig,
    ...requestPaymentMethodConfig,
  ];

  const { renderFormElements, ctx } = useForm<FormValues & T, SerializedFormValues & T>({
    initialValues,
    isReviewing: reviewing,
    config: [
      ...infoCollectionMethodConfig,
      ...accountTypeConfig,
      ...baseConfig,
      ...moreInfoConfig,
      ...primaryContactConfig,
      ...addressConfig,
      ...connectionTypeConfig,
      ...paymentMethodConfig,
    ],
    onSubmit,
  });

  const isIndividual = useMemo(() => ctx.values.accountType === 'individual', [ctx.values]);

  function handleFiFilter(filter: string) {
    const filtered = Object.values(fiRes.FinancialInstitutions || {}).filter((fi) => {
      if (!filter) return true;

      return `${fi.FinancialInstitutionID}${
        fi.FinancialInstitutionNumber
      }${fi.FIName.toLowerCase()}${fi.FINameFrench.toLowerCase()}`.includes(filter.toLowerCase());
    });

    // add an option that's prefilled with the user's manually entered number to allow them to select it
    if (filtered.length === 0 && filter !== '' && !isNaN(Number(filter))) {
      setFiOptions([
        {
          id: filter,
          number: filter,
          label: '(Manually Entered)',
        },
      ]);
    } else {
      setFiOptions(filtered.map(convertToFiOption));
    }
  }

  function handleFiBranchFilter(filter: string) {
    const filtered = Object.values(fiBranchRes.Branches || {}).filter((branch) => {
      if (!filter) return true;

      return `${
        branch.BranchTransitNumber
      }${branch.Address.toLowerCase()}${branch.TownCity.toLowerCase()}${branch.PostalCode.toLowerCase()}${branch.Province.toLowerCase()}`.includes(
        filter.toLowerCase(),
      );
    });

    // add an option that's prefilled with the user's manually entered number to allow them to select it
    if (filtered.length === 0 && filter !== '' && !isNaN(Number(filter))) {
      setFiBranchOptions([
        {
          transitNumber: filter,
          label: '(Manually Entered)',
        },
      ]);
    } else {
      setFiBranchOptions(filtered.map(convertToFiBranchOption));
    }
  }

  // fallback to return fiID because if the user manually enters a number, the user input (fiID) will be the branch number
  function getFiNum(fiID: string) {
    return fiOptions.find((fi) => fi.id === fiID)?.number || fiID;
  }

  async function handleAddLabelClick() {
    const newLabel = await dialog.open<string>(({ onConfirm, onCancel }) => (
      <AddLabelDialog onClose={onCancel} onAdd={onConfirm} />
    ));

    if (newLabel) {
      ctx.setFieldValue('label', newLabel);
    }
  }

  return {
    ctx,
    renderFormElements,
    infoCollectionMethodConfig,
    accountTypeConfig,
    baseConfig,
    moreInfoConfig,
    primaryContactConfig,
    addressConfig,
    connectionTypeConfig,
    paymentMethodConfig,
  };
}

function convertToFiOption(fi: FinancialInstitution): FiOption {
  return {
    id: fi.FinancialInstitutionID,
    number: fi.FinancialInstitutionNumber,
    label: fi.FIName,
  };
}

function convertToFiBranchOption(branch: FinancialInstitutionBranch): FiBranchOption {
  return {
    transitNumber: branch.BranchTransitNumber.toString(),
    label: [branch.Address, branch.TownCity, branch.PostalCode, branch.Province].filter((val) => !!val).join(', '),
  };
}
